import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown } from "lucide-react";
import { useRef, useState } from "react";

import { api } from "../api/client";
import type { UnitItems } from "../api/types";
import { adminErrorText } from "../auth/admin/adminText";
import { ReauthCancelled, withReauth } from "../auth/admin/reauth";
import { designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Checkbox } from "../components/Checkbox";
import { Dialog, DialogBody } from "../components/Dialog";
import { Motion, Presence } from "../components/presence";
import { Select } from "../components/Select";
import { Button, inputClass, SectionLabel } from "../components/ui";
import { t } from "../i18n/i18n";
import { plural } from "../plural";
import { useUi } from "../state/ui";
import { Waiting } from "../components/Waiting";

/** « Attribuer un propriétaire… » aux éléments de l'unité (comptes nominatifs, docs/17 §3.3 règle 4, §5.6, lot 5) :
 *  dossiers, ordres de bataille construits et terrains enregistrés avant la bascule, qui n'ont pas de propriétaire.
 *  L'administrateur coche des éléments et choisit un compte actif ; l'attribution ne retire l'accès à personne
 *  (l'élément reste modifiable par toute l'unité) et seul le nouveau propriétaire pourra ensuite en restreindre le
 *  partage ou la visibilité. Geste d'administration : ré-authentification de 10 minutes (`withReauth`). */

type Kind = "exercises" | "odbs" | "zones";

const SECTIONS: { kind: Kind; label: string }[] = [
  { kind: "exercises", label: "Dossiers" },
  { kind: "odbs", label: "Ordres de bataille" },
  { kind: "zones", label: "Terrains" },
];

/** Éléments d'une liste, sous une forme commune (identifiant : code d'ODB, sinon uuid). */
export function unitEntries(items: UnitItems | undefined, kind: Kind): { id: string; name: string }[] {
  if (!items) return [];
  if (kind === "odbs") return items.odbs.map((o) => ({ id: o.code, name: o.name }));
  return items[kind].map((e) => ({ id: e.id, name: e.name }));
}

/** Nombre d'éléments cochés, toutes listes confondues. */
export function selectedCount(selected: Record<Kind, string[]>): number {
  return selected.exercises.length + selected.odbs.length + selected.zones.length;
}

const NONE: Record<Kind, string[]> = { exercises: [], odbs: [], zones: [] };

export function UnitItemsDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const items = useQuery({ queryKey: ["unit-items"], queryFn: api.sharing.unitItems, staleTime: 0 });
  const directory = useQuery({ queryKey: ["accounts-directory"], queryFn: api.accounts.directory, staleTime: 60_000 });
  const [selected, setSelected] = useState<Record<Kind, string[]>>(NONE);
  const [owner, setOwner] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const ownerRef = useRef<HTMLDivElement>(null);
  const count = selectedCount(selected);
  const ownerName = directory.data?.find((a) => a.id === owner)?.display_name ?? "";
  const total = SECTIONS.reduce((n, s) => n + unitEntries(items.data, s.kind).length, 0);

  const assign = useMutation({
    mutationFn: () => withReauth(() => api.sharing.assign({ account_id: owner, ...selected })),
    onSuccess: (result) => {
      const { exercises, odbs, zones } = result;
      queryClient.setQueryData<UnitItems>(["unit-items"], { exercises, odbs, zones });
      // Listes qui montrent le propriétaire : dossiers, ODB, terrains, éléments des comptes.
      for (const key of [["exercises"], ["odbs"], ["zones"], ["unit-account-items"]])
        void queryClient.invalidateQueries({ queryKey: key });
      const n = result.assigned.exercises + result.assigned.odbs + result.assigned.zones;
      notify({
        text: t("{elements} à {nom}", {
          elements: plural(n, t("élément attribué"), t("éléments attribués")),
          nom: ownerName,
        }),
        detail: t("Ils restent modifiables par toute l'unité, jusqu'à ce que leur propriétaire en change le partage."),
        tone: "ok",
      });
      setSelected(NONE);
    },
  });
  const failure = assign.error && !(assign.error instanceof ReauthCancelled) ? assign.error : null;

  const toggle = (kind: Kind, id: string, on: boolean) =>
    setSelected((s) => ({ ...s, [kind]: on ? [...s[kind], id] : s[kind].filter((x) => x !== id) }));
  const ids = (kind: Kind) => unitEntries(items.data, kind).map((e) => e.id);
  const all = (on: boolean) => setSelected(on ? { exercises: ids("exercises"), odbs: ids("odbs"), zones: ids("zones") } : NONE);

  return (
    <Dialog
      title={t("Éléments de l'unité")}
      subtitle={t("Enregistrés avant les comptes nominatifs, sans propriétaire")}
      width={600}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Fermer")}
          </Button>
          <Button
            disabled={assign.isPending}
            blocked={total === 0 || count === 0 || !owner}
            onBlocked={() => {
              // Préalable manquant : les éléments à cocher, puis le compte (action attendue désignée, R12).
              if (total === 0) return onClose();
              if (count === 0) {
                designate(listRef.current, "ring");
                notify({ text: t("Cochez les éléments à attribuer"), tone: "info" });
              } else {
                designate(ownerRef.current?.querySelector("select") ?? null, "field");
                notify({ text: t("Choisissez d'abord le nouveau propriétaire"), tone: "info" });
              }
            }}
            onClick={() => assign.mutate()}
          >
            <Crown size={14} /> {assign.isPending ? <Waiting>{t("Attribution…")}</Waiting> : t("Attribuer")}
          </Button>
        </>
      }
    >
      <DialogBody>
        {items.error ? (
          <Callout tone="danger">{adminErrorText(items.error)}</Callout>
        ) : !items.data ? (
          <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>
        ) : total === 0 ? (
          <Motion origin="fade">
            <p className="text-ui text-ink-muted">{t("Tous les éléments de l'unité ont un propriétaire.")}</p>
          </Motion>
        ) : (
          <Motion origin="fade" className="space-y-3">
            <div ref={listRef} className="space-y-3 rounded-lg">
              <Checkbox
                checked={count === 0 ? false : count === total ? true : "mixed"}
                onChange={(on) => all(on)}
                label={t("Tout cocher")}
                className="min-h-11"
              />
              {SECTIONS.map(({ kind, label }) => {
                const entries = unitEntries(items.data, kind);
                if (entries.length === 0) return null;
                return (
                  <div key={kind} className="space-y-1 border-t border-line pt-3">
                    <SectionLabel>{t(label)}</SectionLabel>
                    <ul>
                      {entries.map((entry) => (
                        <li key={entry.id}>
                          <Checkbox
                            checked={selected[kind].includes(entry.id)}
                            onChange={(on) => toggle(kind, entry.id, on)}
                            label={entry.name || "—"}
                            className="min-h-11 w-full"
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div ref={ownerRef} className="space-y-1.5 border-t border-line pt-3">
              <SectionLabel>{t("Nouveau propriétaire")}</SectionLabel>
              <Select
                className={inputClass}
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder={t("Choisir un compte…")}
                aria-label={t("Nouveau propriétaire")}
              >
                {(directory.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.appointment ? `${a.display_name} · ${a.appointment}` : a.display_name}
                  </option>
                ))}
              </Select>
            </div>
            <p className="text-meta leading-snug text-ink-muted">
              {t("Attribuer un propriétaire ne retire l'accès à personne : l'élément reste modifiable par toute l'unité, jusqu'à ce que son propriétaire en change le partage.")}
            </p>
          </Motion>
        )}
        <Presence show={Boolean(failure)}>{failure && <Callout tone="danger">{adminErrorText(failure)}</Callout>}</Presence>
      </DialogBody>
    </Dialog>
  );
}
